import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import CalendarBody from "./components/CalendarBody";
import CalendarHeader from "./components/CalendarHeader";
import { dateUtils, GRID_CONFIG, styleGenerators } from "./utils/calendarUtils";
import {
  createDragDropHandlers,
  createPlanMoveHandler,
} from "./utils/dragDropUtils";

// Main Calendar Component
const OptimizedCalendar = ({ calendarData, setCalendarData, refetch }) => {
  const navigate = useNavigate();

  // State management
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dropActiveIndex, setDropActiveIndex] = useState(null);
  const [droppingIndex, setDroppingIndex] = useState(null);

  // Memoized calculations
  const gridConfig = useMemo(() => {
    const { DAY_WITH_DUTY_COLUMNS, LINE_LIST_COLUMNS } = GRID_CONFIG;
    const days =
      calendarData.calendarHeader.days.length * DAY_WITH_DUTY_COLUMNS +
      LINE_LIST_COLUMNS;

    return {
      days,
      dayWithDutyColumns: DAY_WITH_DUTY_COLUMNS,
      lineListColumns: LINE_LIST_COLUMNS,
    };
  }, [calendarData.calendarHeader.days.length]);

  // Event handlers
  const handlePlanDoubleClick = useCallback(
    (plan, dateObj, line) => {
      console.log("Plan double-clicked:", { plan, dateObj, line });

      // Get plan ID with fallback options
      const planId = plan.id || plan.plan_id;

      if (dateUtils.isPastDate(dateObj.date)) return;

      // Allow editing of plans even on past dates
      if (planId) {
        console.log(
          "Navigating to plan update:",
          `/production-planning/plan-update/${planId}`,
        );
        navigate(`/production-planning/plan-update/${planId}`);
      } else {
        // Prevent new plan creation on past dates, weekends, holidays, and fully planned days
        if (dateUtils.isPastDate(dateObj.date)) {
          console.log("Cannot create new plans on past dates");
          return;
        }
        if (dateObj.week_status === "weekend") {
          console.log("Cannot create new plans on weekends");
          return;
        }
        if (dateObj.week_status === "holiday") {
          console.log("Cannot create new plans on holidays");
          return;
        }
        if (dateObj.is_full_day_planned) {
          console.log("Cannot create new plans on fully planned days");
          return;
        }
        console.log(
          "Navigating to plan entry:",
          `/production-planning/plan-entry?line_id=${line.line_id}&start_date=${dateObj.date}`,
        );
        navigate(
          `/production-planning/plan-entry?line_id=${line.line_id}&start_date=${dateObj.date}`,
        );
      }
    },
    [navigate],
  );

  // Create handlers using utility functions
  const handlePlanMove = useMemo(
    () => createPlanMoveHandler(calendarData, refetch),
    [calendarData, refetch],
  );

  const dragDropHandlers = useMemo(
    () =>
      createDragDropHandlers(
        setDraggedIndex,
        setDropActiveIndex,
        setDroppingIndex,
      ),
    [],
  );

  const dragDropProps = useMemo(
    () => ({
      draggedIndex,
      setDraggedIndex,
      setDropActiveIndex,
      setDroppingIndex,
      onPlanMove: handlePlanMove,
    }),
    [draggedIndex, handlePlanMove],
  );

  return (
    <div
      style={styleGenerators.getCalendarMainStyle(
        gridConfig.days,
        calendarData.calendarBody.length,
      )}
    >
      <CalendarHeader calendarData={calendarData} gridConfig={gridConfig} />
      <CalendarBody
        calendarData={calendarData}
        gridConfig={gridConfig}
        dropActiveIndex={dropActiveIndex}
        droppingIndex={droppingIndex}
        onPlanDoubleClick={handlePlanDoubleClick}
        dragDropProps={dragDropProps}
        dragDropHandlers={dragDropHandlers}
      />
    </div>
  );
};

export default OptimizedCalendar;
